<template>
  <div class="flex flex-col">
    <!-- Label and Description -->
    <div v-if="label || description" class="mb-2">
      <div class="flex items-center space-x-2 mb-1">
        <div :class="['flex items-center', labelIconPosition === 'left' ? 'flex-row' : 'flex-row-reverse']">
          <icon-component v-if="labelIconPosition === 'left' && labelIcon" :icon="labelIcon" :class="[labelIconClass, 'mr-1', iconClickableClass]" @click="emitIconClick"/>
          <label v-if="label" :for="inputId" :class="[labelClass, additionalLabelClass]" class="text-sm"> {{ label }}<span v-if="required" class="text-primary">*</span> </label>
          <icon-component v-if="labelIconPosition === 'right' && labelIcon" :icon="labelIcon" :class="[labelIconClass, 'ml-1', iconClickableClass]" @click="emitIconClick"/>
        </div>
        <img v-if="showAiIcon" :src="aiStarIcon" class="cursor-pointer w-4 hover:opacity-70 transition-opacity" @click="emitIconClick" alt="star icon"/>
      </div>
      <p v-if="description" :class="[descriptionClass, additionalDescriptionClass]" class="text-xs mb-1">{{ description }}</p>
    </div>

    <!-- Select Component -->
    <div class="custom-select relative" ref="reference">
      <button type="button" @click="toggleDropdown" :class="[
          'flex items-center justify-between border bg-white rounded-md focus:outline-none w-full',
          sizeClasses,
          error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-gray-500 focus:ring-1 focus:ring-gray-500'
        ]">
        <span class="truncate">{{ selectedOption ? selectedOption.label : placeholder }}</span>
        <div class="flex items-center">
          <span v-if="clearable && hasSelectedValue" class="material-symbols-outlined text-gray-400 hover:text-gray-600 mr-1 cursor-pointer" :class="[iconSizeClass]" @click.stop="clearSelection">
            close
          </span> <span :class="['material-symbols-outlined text-gray-400', iconSizeClass]">expand_more</span>
        </div>
      </button>

      <!-- Dropdown Menu with Teleport to body -->
      <Teleport to="body">
        <div v-if="isOpen" ref="floating" :style="floatingStyles" class="z-[9999] bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto thin-scrollbar mt-1 floating-dropdown" @click.stop>
          <!-- Arama Input'u -->
          <div v-if="showSearch" class="px-2 py-2 border-b border-gray-200">
            <div class="relative">
              <input v-model="searchQuery" placeholder="Search" type="text" class="w-full text-sm border border-gray-300 rounded-md py-0.5 px-2 pr-7 focus:outline-none focus:ring-1 focus:ring-primary" @click.stop @input="filterOptions" autocomplete="off"/>
              <span class="material-symbols-outlined text-gray-400 absolute right-2 top-1/2 transform -translate-y-1/2 !text-[16px]">search</span>
            </div>
          </div>

          <ul>
            <li v-for="option in filteredOptions" :key="option.value" @click="selectOption(option)" :class="[
                  'hover:bg-gray-100 cursor-pointer truncate',
                  sizeClasses
                ]">
              {{ option.label }}
            </li>
            <li v-if="filteredOptions.length === 0" :class="['text-gray-500 text-center py-2', sizeClasses]">
              {{ noOptionsMessage }}
            </li>
          </ul>
        </div>
      </Teleport>
    </div>

    <!-- Error Message -->
    <div v-if="showError" class="h-3">
      <div v-if="error" class="flex items-start gap-1">
        <span class="material-symbols-outlined text-red-500 !text-[14px] mt-0.5">error</span>
        <p class="text-xs text-red-500">{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { computePosition, flip, shift, offset } from '@floating-ui/dom'
import IconComponent from '@/components/common/VIcon.vue'
import { v4 as uuidv4 } from 'uuid'

const props = defineProps({
  modelValue: [String, Number],
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: '' },
  label: { type: String, default: '' },
  description: { type: String, default: '' },
  required: { type: Boolean, default: false },
  error: { type: String, default: '' },
  showError: { type: Boolean, default: false },
  clearable: { type: Boolean, default: false },
  noOptionsMessage: { type: String, default: 'No options available' },
  size: {
    type: String,
    default: 'sm',
    validator: (value) => ['sm', 'md', 'lg'].includes(value),
  },
  labelClass: { type: String, default: 'font-semibold text-secondary' },
  descriptionClass: { type: String, default: 'text-gray-600' },
  additionalDescriptionClass: { type: String, default: '' },
  additionalLabelClass: { type: String, default: '' },
  showAiIcon: { type: Boolean, default: false },
  labelIcon: { type: String, default: '' },
  labelIconClass: { type: String, default: 'text-primary !text-[20px]' },
  labelIconPosition: {
    type: String,
    default: 'left',
    validator: (value) => ['left', 'right'].includes(value),
  },
  iconClickable: { type: Boolean, default: false },
  showSearch: { type: Boolean, default: false },
  name: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'iconClicked', 'clear'])

// ID oluştur - name prop'u varsa onu kullan, yoksa benzersiz bir ID olutur
const inputId = computed(() => {
  // Eğer name veya modelValue varsa, tahmin edilebilir ID oluştur
  if (props.name) {
    return `select-${props.name}`
  }
  // Benzersiz bir ID oluştur
  return `select-${Math.random().toString(36).substring(2, 9)}`
})

const isOpen = ref(false)
const searchQuery = ref('')
const reference = ref(null)
const floating = ref(null)
const aiStarIcon = require('@/assets/icons/aiStar.png')

// Floating UI için stil özellikleri
const floatingStyles = ref({
  position: 'fixed',
  top: '0',
  left: '0',
  width: 'auto',
  visibility: 'hidden',
  zIndex: 99999, // Ensure it's above modal
})

// Seçilen option'u hesapla
const selectedOption = computed(() =>
    props.options.find(option => option.value === props.modelValue),
)

// Default option seçili mi kontrol et
const hasSelectedValue = computed(() => {
  return props.modelValue !== null &&
      props.modelValue !== undefined &&
      props.modelValue !== ''
})

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'text-xs py-1.5 px-2'
    case 'md':
      return 'text-sm py-2 px-3'
    case 'lg':
      return 'text-base py-2.5 px-3'
    default:
      return 'text-sm py-2 px-3'
  }
})

const iconSizeClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return '!text-[16px]'
    case 'md':
      return '!text-[18px]'
    case 'lg':
      return '!text-[20px]'
    default:
      return '!text-[18px]'
  }
})

const iconClickableClass = computed(() => {
  return props.iconClickable ? 'cursor-pointer hover:text-blue-600 transition-colors' : ''
})

// Seçenekleri arama sorgusuna göre filtrele
const filterOptions = () => {
  if (!searchQuery.value.trim()) {
    // Arama yoksa tüm seçenekleri göster
    filteredOptions.value = [...props.options]
    return
  }

  // Case-insensitive arama
  const query = searchQuery.value.toLowerCase().trim()

  filteredOptions.value = props.options.filter(option => {
    // Label'da arama yap (string değilse toString ile dönüştür)
    const label = option.label?.toString().toLowerCase() || ''
    return label.includes(query)
  })
}

const clearSelection = () => {
  emit('update:modelValue', null)
  emit('clear')
}

const updatePosition = async () => {
  if (!reference.value || !floating.value) return

  try {
    const middleware = [
      offset(4),
      flip({
        fallbackPlacements: ['top-start', 'bottom-start'],
        padding: 8,
      }),
      shift({ padding: 8 }),
    ]

    const triggerRect = reference.value.getBoundingClientRect()
    const { x, y, placement, strategy } = await computePosition(
        reference.value,
        floating.value,
        {
          placement: 'bottom-start',
          middleware,
        },
    )

    // Update styles for fixed positioning
    floatingStyles.value = {
      position: 'fixed',
      top: `${y}px`,
      left: `${x}px`,
      width: `${triggerRect.width}px`,
      visibility: 'visible',
      zIndex: 99999,
    }
  } catch (error) {
    console.error('Error updating position:', error)
  }
}

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    // Dropdown açıldığında aramayı sıfırla ve tüm seçenekleri göster
    searchQuery.value = ''
    filteredOptions.value = [...props.options]

    nextTick(async () => {
      await updatePosition()
    })
  }
}

const selectOption = (option) => {
  emit('update:modelValue', option.value)
  isOpen.value = false
}

const handleClickOutside = (event) => {
  if (isOpen.value &&
      reference.value &&
      floating.value &&
      !reference.value.contains(event.target) &&
      !floating.value.contains(event.target)) {
    isOpen.value = false
  }
}

const emitIconClick = () => {
  emit('iconClicked')
}

// Filter Options for initial setup
const filteredOptions = ref([])

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('scroll', updatePosition, true)
  window.addEventListener('resize', updatePosition)

  // Başlangıçta filtreleme için tüm seçenekleri yükle
  filteredOptions.value = [...props.options]
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('scroll', updatePosition, true)
  window.removeEventListener('resize', updatePosition)
})

watch(() => props.options, () => {
  // Options değiştiğinde filtrelemeyi güncelle
  filteredOptions.value = [...props.options]

  if (isOpen.value) {
    nextTick(async () => {
      await updatePosition()
    })
  }
}, { deep: true })

// Arama sorgusu değiştiğinde filtrelemeyi güncelle
watch(() => searchQuery.value, () => {
  filterOptions()
  if (isOpen.value) {
    nextTick(async () => {
      await updatePosition()
    })
  }
})

// Watch for visibility changes
watch(isOpen, async (newValue) => {
  if (newValue) {
    await nextTick()
    await updatePosition()

    // Add event listeners for scroll and resize
    document.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
  } else {
    // Remove event listeners when dropdown is closed
    document.removeEventListener('scroll', updatePosition, true)
    window.removeEventListener('resize', updatePosition)
  }
})
</script>

<style scoped>
/* Modal animasyonları */
@keyframes modalFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.floating-dropdown {
  animation: modalFadeIn 0.1s ease-out;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>